import { createStyles, Group, Modal, Stack } from '@mantine/core';
import { useOs } from '@mantine/hooks';
import { Selector, SelectorTarget, SelectType } from '@repo/common/components';
import { Banner, Button, Text } from '@repo/foundations';
import { DataTable } from '@repo/mantine-datatable';
import { useState } from 'react';
import {
	HEADER_HEIGHT,
	ROW_HEIGHT,
	useTableStyles,
} from '../../../TableV2/TableV2.styles';
import type { ColumnMappingRecord } from '../ImportSettings';

interface ColumnMappingModalProps {
	opened: boolean;
	onClose: () => void;
	onContinue: () => void;
	fileColumnNames: string[];
	targetColumns: { value: string; label: string }[];
	columnMapping: Record<string, string>;
	setColumnMapping: (
		columnMapping:
			| Record<string, string>
			| ((prev: Record<string, string>) => Record<string, string>)
	) => void;
	selectedColumns: {
		selectedRecords: ColumnMappingRecord[];
		lastSelectedIndex: number | null;
	};
	setSelectedColumns: (
		selectedColumns:
			| {
					selectedRecords: ColumnMappingRecord[];
					lastSelectedIndex: number | null;
			  }
			| ((prev: {
					selectedRecords: ColumnMappingRecord[];
					lastSelectedIndex: number | null;
			  }) => {
					selectedRecords: ColumnMappingRecord[];
					lastSelectedIndex: number | null;
			  })
	) => void;
}

const useStyles = createStyles((theme) => ({
	button: {
		'& .mantine-Button-inner': {
			paddingRight: theme.spacing['3xs'],
			paddingTop: theme.spacing['3xs'],
			paddingBottom: theme.spacing['3xs'],
			paddingLeft: theme.spacing.xs,
		},
		marginRight: -4,
		height: 'auto',
		padding: 0,
	},
}));

export function ColumnMappingModal({
	opened,
	onClose,
	onContinue,
	fileColumnNames,
	targetColumns,
	columnMapping,
	setColumnMapping,
	selectedColumns,
	setSelectedColumns,
}: ColumnMappingModalProps) {
	const { classes: tableClasses } = useTableStyles({
		hideCheckbox: false,
		noBorder: true,
		disableSelectedDisplay: true,
		withHover: true,
	});
	const { classes } = useStyles();
	const [error, setError] = useState<string>('');

	let tableMaxHeight = HEADER_HEIGHT + 6 * ROW_HEIGHT;

	const os = useOs();
	if (!!tableMaxHeight && os === 'windows') {
		tableMaxHeight += 20;
	}

	const records = fileColumnNames.map((col) => ({
		csvColumns: col,
		secodaFields: columnMapping[col] || '',
	}));

	const onClick = () => {
		const columnsWithSelect = selectedColumns.selectedRecords
			.filter((record) => record.secodaFields === 'select')
			.map((record) => record.csvColumns);

		if (columnsWithSelect.length > 0) {
			const prefix =
				columnsWithSelect.length === 1
					? `"${columnsWithSelect[0]}" column`
					: 'Columns';
			setError(
				`${prefix} must either be matched with a Secoda field or deselected.`
			);
			return;
		}

		setError('');
		onContinue();
	};

	const handleColumnMappingChange = (column: string, value: string) => {
		setColumnMapping((prev) => ({
			...prev,
			[column]: value,
		}));
		setSelectedColumns((prev) => ({
			...prev,
			selectedRecords: prev.selectedRecords.map((record) =>
				record.csvColumns === column
					? { ...record, secodaFields: value }
					: record
			),
		}));
	};

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title="Import Properties"
			size="lg"
		>
			<Stack spacing="md">
				<Text size="sm">
					Fields have been auto-matched where possible. Review and adjust the
					mappings before proceeding with the import.
				</Text>

				{error !== '' && <Banner tone="critical" message={error} />}

				<DataTable<ColumnMappingRecord>
					// Styling
					withStickyColumnBorder
					noHeader={false}
					withBorder
					borderRadius="md"
					maxHeight={tableMaxHeight}
					height={tableMaxHeight}
					classNames={tableClasses}
					// Select
					selectedRecordsState={selectedColumns}
					onSelectedRecordsStateChange={setSelectedColumns}
					idAccessor="csvColumns"
					// Data
					columns={[
						{ accessor: 'csvColumns', title: 'Columns in CSV' },
						{
							accessor: 'secodaFiends',
							title: 'Secoda fields',
							textAlignment: 'right',
							render: (row) => (
								<Selector
									initialValue={[columnMapping[row.csvColumns]]}
									getItems={async () => targetColumns}
									onChange={(values) =>
										handleColumnMappingChange(
											row.csvColumns,
											values[0] || 'select'
										)
									}
									selectType={SelectType.Single}
									disableSearch={true}
								>
									<SelectorTarget
										variant={
											columnMapping[row.csvColumns] === 'select'
												? 'default'
												: 'tertiary'
										}
										className={classes.button}
									>
										<Group spacing="2xs">
											<Text size="sm">
												{
													targetColumns.find(
														(item) =>
															item.value === columnMapping[row.csvColumns]
													)?.label
												}
											</Text>
										</Group>
									</SelectorTarget>
								</Selector>
							),
						},
					]}
					records={records}
				/>

				<Group position="right">
					<Button variant="default" onClick={onClose}>
						Cancel
					</Button>
					<Button variant="primary" onClick={onClick}>
						Import {selectedColumns.selectedRecords.length}{' '}
						{selectedColumns.selectedRecords.length === 1
							? 'property'
							: 'properties'}
					</Button>
				</Group>
			</Stack>
		</Modal>
	);
}
